import { AnimatePresence } from 'framer-motion';
import * as htmlToImage from 'html-to-image';
import { FC, memo, useEffect, useState } from 'react';

import PandaWatermark from '@/assets/icons/cover-images/panda-watermark.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const setCrossOriginForExternalImages = (element: HTMLElement) => {
      element.querySelectorAll('img').forEach((img) => {
        const src = img.currentSrc || img.src;
        const isData = src.startsWith('data:');
        const isBlob = src.startsWith('blob:');
        const isSameOrigin = src.startsWith(window.location.origin);
        if (!isData && !isBlob && !isSameOrigin) {
          img.crossOrigin = 'anonymous';
        }
      });
    };

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      const holder = document.createElement('div');
      holder.classList.add('generated-images-holder-var16');
      document.body.appendChild(holder);

      const imageHolder = document.createElement('div');
      imageHolder.classList.add('image-holder-var16');

      if (variation.topText) {
        const topText = document.createElement('div');
        topText.classList.add('top-text-var16');
        let text = variation.topText;
        if (variation.topTextBrakes) {
          topText.innerText = text;
        } else {
          topText.innerHTML = text;
        }
        imageHolder.appendChild(topText);
      }

      if (variation.bottomText) {
        const bottomText = document.createElement('div');
        const bottomTextP = document.createElement('p');
        bottomText.classList.add('bottom-text-var16');
        let text = variation.bottomText;
        if (variation.bottomTextBrakes) {
          bottomTextP.innerText = text;
        } else {
          text = distributeTextCanvas(text, {
            maxWidth: 660,
            fontFamily: '"CustomArial", Arial',
            minFontSize: variation.fontSize !== 0 ? variation.fontSize : 36,
          });
          bottomTextP.innerHTML = text;
        }
        bottomText.appendChild(bottomTextP);
        imageHolder.appendChild(bottomText);

        if (variation.fontSize) {
          bottomText.style.fontSize = `${variation.fontSize}px`;
        }
      }
      if (variation.watermark) {
        const pandaWatermark = new Image();
        pandaWatermark.classList.add('watermark-var16');
        pandaWatermark.src = PandaWatermark;
        imageHolder.appendChild(pandaWatermark);
      }

      const fullImage = new Image();
      fullImage.classList.add('full-image-var16');
      fullImage.src = variation.imageSrc;
      imageHolder.appendChild(fullImage);

      const watermark = createWatermarkTagImage(
        'actual-photo-var16',
        variation.watermarkTag,
        variation.watermarkPosition,
      );
      if (watermark) imageHolder.appendChild(watermark);

      if (fullImage.decode) {
        try {
          await fullImage.decode();
        } catch {}
      } else {
        await new Promise<void>((res) => {
          if (fullImage.complete) return res();
          fullImage.onload = () => res();
          fullImage.onerror = () => res();
        });
      }

      holder.appendChild(imageHolder);

      setCrossOriginForExternalImages(holder);

      try {
        const imageDataUrl = await htmlToImage.toJpeg(holder, {
          pixelRatio: 1,
          quality: 0.85,
        });

        return imageDataUrl;
      } catch (e) {
        alerts.error();
      } finally {
        setCounter((prev) => prev + 1);
        holder.remove();
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);

      const imagesDataUrls = await Promise.all(
        variations.map((variation) => generateImageVariation(variation)),
      );

      const validImages = imagesDataUrls.filter(
        (url): url is string => url !== undefined,
      );

      setImages(validImages);

      setShowProgress(false);

      setCounter(0);
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
